import React from 'react'
import styled from 'styled-components'
import { Redirect } from 'react-router-dom'
import Header from '../common/header'
import Footer from '../common/footer'
import StoreLinks from '../common/store_links'
import { LocalizedLink as Link } from '../lib/i18n'
import { LIMITATIONS } from '../lib/facts'
import { formatDate } from '../blog/format_date'
import ComparisonTable, { SourceLink } from './table'
import { COMPETITORS, LAST_VERIFIED, SOURCES } from './competitors'
import { COMPARE_PAGES, COMPARE_PUBLISHED, comparePage } from './pages'

const Container = styled.div`
    max-width: 860px;
    margin: 0 auto;
    padding: 0 1.5rem;
`

const Hero = styled.div`
    background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
    border-bottom: 1px solid #e2e8f0;
    padding: 50px 0 40px;

    h1 {
        font-size: 2.8rem;
        font-weight: 800;
        color: #0f172a;
        letter-spacing: -1px;
        margin-bottom: 14px;
    }

    p {
        color: #475569;
        font-size: 1.08rem;
        line-height: 1.7;
    }

    @media (max-width: 640px) {
        padding: 32px 0 30px;

        h1 {
            font-size: 2rem;
        }
    }
`

const Byline = styled.p`
    && {
        font-size: 0.9rem;
        color: #94a3b8;
        margin-bottom: 20px;
    }
`

const Section = styled.section`
    padding: 42px 0 0;

    h2 {
        font-size: 1.7rem;
        font-weight: 800;
        color: #0f172a;
        margin-bottom: 14px;
        letter-spacing: -0.4px;
        line-height: 1.3;
    }

    h3 {
        font-size: 1.2rem;
        font-weight: 700;
        color: #0f172a;
        margin: 28px 0 6px;
    }

    p,
    li {
        color: #475569;
        line-height: 1.75;
    }

    p {
        margin-bottom: 14px;
    }

    @media (max-width: 640px) {
        h2 {
            font-size: 1.4rem;
        }
    }
`

const Verdict = styled.p`
    && {
        font-size: 1.3rem;
        font-weight: 700;
        line-height: 1.5;
        padding: 20px 24px;
        background: #f8fafc;
        border-left: 4px solid #209fff;
        border-radius: 8px;
    }
`

const Checked = styled.p`
    && {
        color: #94a3b8;
        font-size: 0.85rem;
    }
`

const Label = styled.strong`
    color: #0f172a;
`

const ChooseIf = styled.p`
    && {
        padding: 12px 16px;
        background: rgba(32, 159, 255, 0.05);
        border-radius: 8px;
    }
`

const FallsShort = styled.div`
    margin-top: 10px;
    padding: 22px 26px 8px;
    border: 1px solid #e2e8f0;
    border-radius: 14px;

    ul {
        margin-left: 1.2rem;
    }
`

const Download = styled.div`
    display: flex;
    justify-content: center;
    padding: 50px 0 70px;
`

const Sourced = ({ text, sources = [] }) => (
    <p>
        {text}
        {sources.map((href) => (
            <SourceLink key={href} href={href} />
        ))}
    </p>
)

const AppBlock = ({ id, choose }) => {
    const app = COMPETITORS[id]
    const external = id !== 'keybreak'

    return (
        <div>
            <h3>
                <a href={app.url} target={external ? '_blank' : undefined} rel="noopener noreferrer">
                    {app.name}
                </a>
            </h3>
            <p>
                <Label>Best for:</Label> {app.bestFor}
            </p>
            <p>
                <Label>Watch out:</Label> {app.watchOut}
                <SourceLink href={app.sourceUrl} />
            </p>
            <ChooseIf>
                <Label>Choose {app.name} if</Label> {choose}
            </ChooseIf>
        </div>
    )
}

const ComparePage = ({ match }) => {
    const page = comparePage(match.params.slug)
    if (!page) return <Redirect to="/blog/" />

    // Head-to-head pages name the rival in the routing heading; roundups do not.

    const rival = page.apps.length === 2 ? page.apps.find((id) => id !== 'keybreak') : null

    const related = COMPARE_PAGES.filter(({ slug }) => slug !== page.slug)

    return (
        <>
            <Header />
            <Hero>
                <Container>
                    <h1>{page.h1}</h1>
                    <Byline>
                        {formatDate(COMPARE_PUBLISHED, 'en-GB')}
                    </Byline>
                    {page.intro.map((paragraph) => (
                        <Sourced key={paragraph.text} {...paragraph} />
                    ))}
                </Container>
            </Hero>
            <Container>
                <Section>
                    <h2>Quick verdict</h2>
                    <Verdict>{page.verdict}</Verdict>
                    <ComparisonTable apps={page.apps} />
                    <Checked>Verified against each app’s own documentation on {LAST_VERIFIED}.</Checked>
                </Section>

                {page.analysis.map(({ heading, body }) => (
                    <Section key={heading}>
                        <h2>{heading}</h2>
                        {body.map((paragraph) => (
                            <Sourced key={paragraph.text} {...paragraph} />
                        ))}
                    </Section>
                ))}

                <Section>
                    <h2>{page.apps.length === 2 ? 'Which to choose' : 'The apps, one by one'}</h2>
                    {page.apps.map((id) => (
                        <AppBlock key={id} id={id} choose={page.choose[id]} />
                    ))}
                </Section>

                <Section>
                    <h2>{rival ? `Where ${COMPETITORS[rival].name} fits better` : 'Who should choose something else'}</h2>
                    <FallsShort>
                        <ul>
                            {LIMITATIONS.map(({ text, sources }) => (
                                <li key={text}>
                                    {text}
                                    {sources.map((key) => (
                                        <SourceLink key={key} href={SOURCES[key]} />
                                    ))}
                                </li>
                            ))}
                            {page.fallsShortExtra.map(({ text, sources }) => (
                                <li key={text}>
                                    {text}
                                    {sources.map((href) => (
                                        <SourceLink key={href} href={href} />
                                    ))}
                                </li>
                            ))}
                        </ul>
                    </FallsShort>
                </Section>

                <Section>
                    <h2>More comparisons</h2>
                    <ul>
                        {related.map(({ slug, h1 }) => (
                            <li key={slug}>
                                <Link to={`/blog/${slug}`}>{h1}</Link>
                            </li>
                        ))}
                    </ul>
                    <p>
                        For what Keybreak does on its own terms, see the{' '}
                        <Link to="/break-reminder-app">Keybreak break reminder app</Link> page and{' '}
                        <Link to="/pricing">pricing</Link>.
                    </p>
                </Section>
            </Container>
            <Download>
                <StoreLinks />
            </Download>
            <Footer />
        </>
    )
}

export default ComparePage
